import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import dayjs, { getUserTimezone, formatInTimeZone } from '../lib/dateUtils';
import { Calendar, Clock, Upload, User, Mail, Heart, HelpCircle, FileText, Smartphone, ShieldCheck, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { EMAIL_REGEX } from '../lib/constants';

export const BookingForm = ({ service, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    dob: '',
    gender: '',
    sessionType: '',
    readingFocus: '',
    partnerName: '',
    partnerDob: '',
    questions: '',
    situation: '',
    slot: '', // Will store the original Slot object or unique ID
    email: '',
    picture: null,
    isEmergency: false,
    isEmergency: false,
    bookingDate: '',
    tiktokUsername: ''
  });

  const [emailError, setEmailError] = useState('');

  const [pricingMap, setPricingMap] = useState({});
  const [globalCampaign, setGlobalCampaign] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/api\/?$/, '').replace(/\/$/, '') || 'http://localhost:8000';
        const [sRes, cRes] = await Promise.all([
          axios.get(`${baseUrl}/api/services`),
          axios.get(`${baseUrl}/api/campaign`)
        ]);

        const pMap = {};
        sRes.data.forEach(s => pMap[s.key] = s);
        setPricingMap(pMap);
        setGlobalCampaign(cRes.data);
      } catch (e) {
        console.error("Failed to fetch pricing", e);
      }
    };
    fetchPricing();
  }, []);

  const getPriceDisplay = (key) => {
    const service = pricingMap[key];
    if (!service) return "Loading...";

    let amount = service.amount;
    let original = null;

    const isPredictionsOnly = key === 'tiktok-live';
    if (globalCampaign && globalCampaign.is_active && !isPredictionsOnly) {
      const expiry = new Date(globalCampaign.expiry_date);
      if (expiry > new Date()) {
        original = amount;
        amount = amount * (1 - globalCampaign.discount_percentage / 100);
      }
    }

    const currency = service.currency === 'EUR' ? '€' : service.currency;

    if (original) {
      return `${currency}${amount.toFixed(2)} (was ${currency}${original.toFixed(2)})`;
    }
    return `${currency}${amount.toFixed(2)}`;
  };

  const [userTimezone, setUserTimezone] = useState(getUserTimezone());

  const handleChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    if (name === 'picture') {
      setFormData({ ...formData, [name]: files[0] });
    } else if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
      if (name === 'email') setEmailError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Trim and Validate Email
    const trimmedEmail = formData.email.trim();
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address (e.g., name@domain.com)');
      return;
    }

    const submissionData = {
      ...formData,
      email: trimmedEmail
    };

    onSubmit(submissionData);
  };

  const isLive = service.title.includes('1:1 Zoom');
  const isAura = service.title.includes('Aura');
  const isTikTokLive = service.title.includes('Predictions Only');
  const isDelivered = service.title.includes('Delivered');

  // Slot Logic
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (isLive && formData.bookingDate && formData.sessionType) {
      const fetchSlots = async () => {
        setLoadingSlots(true);
        try {
          const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/api\/?$/, '').replace(/\/$/, '');
          let duration = 20;
          if (formData.sessionType === '40_min') duration = 40;

          // 1. Fetch Selected Date Slots
          const typeParam = formData.isEmergency ? 'emergency' : 'regular';

          const p1 = axios.get(`${baseUrl}/api/slots?date=${formData.bookingDate}&available_only=true&duration=${duration}&type=${typeParam}`);

          let promises = [p1];

          // 2. If Emergency, Fetch Next Day too
          if (formData.isEmergency) {
            const dateObj = new Date(formData.bookingDate);
            dateObj.setDate(dateObj.getDate() + 1);
            const nextDateStr = format(dateObj, 'yyyy-MM-dd');
            const p2 = axios.get(`${baseUrl}/api/slots?date=${nextDateStr}&available_only=true&duration=${duration}&type=emergency`);
            promises.push(p2);
          }

          const results = await Promise.all(promises);

          // Combine
          let combined = results[0].data;
          if (results.length > 1) {
            combined = [...combined, ...results[1].data];
          }

          // DEBUG: Log slots before and after filter
          console.log('Server returned slots:', combined);

          // Filter out canceled slots as per user request
          const filtered = combined.filter(s => s.type !== 'canceled');

          if (combined.length !== filtered.length) {
            console.log('Filtered out canceled slots:', combined.length - filtered.length);
          }

          combined = filtered;
          setAvailableSlots(combined);

        } catch (error) {
          console.error('Error fetching slots:', error);
          setAvailableSlots([]);
        } finally {
          setLoadingSlots(false);
        }
      };

      fetchSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [isLive, formData.sessionType, formData.bookingDate, formData.isEmergency]);

  // Filter slots based on emergency status
  const filteredSlots = availableSlots.filter((slot) => {
    // Note: We already filtered by Type and Date in the API call above.
    return true;
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">

        {/* Common Fields */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-primary flex items-center gap-2">
            <User className="w-4 h-4 text-[var(--color-primary)]" /> Full Name *
          </label>
          <input
            type="text"
            name="name"
            required
            className="w-full p-3 rounded-lg border border-primary/10 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
            placeholder="John Doe"
            onChange={handleChange}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-primary flex items-center gap-2">
            <Mail className="w-4 h-4 text-[var(--color-primary)]" /> Email Address *
          </label>
          <input
            type="email"
            name="email"
            required
            className={`w-full p-3 rounded-lg border focus:ring-2 focus:ring-primary/20 outline-none transition-all ${emailError ? 'border-red-500 focus:border-red-500' : 'border-primary/10 focus:border-primary'
              }`}
            placeholder="your@email.com"
            onChange={handleChange}
          />
          {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
        </div>

        {isTikTokLive && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-primary flex items-center gap-2">
              <User className="w-4 h-4 text-[var(--color-primary)]" /> TikTok / Zoom Username *
            </label>
            <input
              type="text"
              name="tiktokUsername"
              required
              className="w-full p-3 rounded-lg border border-primary/10 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              placeholder="@username"
              onChange={handleChange}
            />
          </div>
        )}

        {!isAura && !isTikTokLive && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium text-primary flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[var(--color-primary)]" /> Date of Birth *
              </label>
              <input
                type="date"
                name="dob"
                required
                className="w-full p-3 rounded-lg border border-primary/10 focus:ring-2 focus:ring-primary/20 outline-none"
                onChange={handleChange}
              />
              <p className="text-xs text-red-400 mt-1">* Must be 18+ to book</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-primary flex items-center gap-2">
                <User className="w-4 h-4 text-[var(--color-primary)]" /> Gender *
              </label>
              <select
                name="gender"
                required
                className="w-full p-3 rounded-lg border border-primary/10 focus:ring-2 focus:ring-primary/20 outline-none"
                onChange={handleChange}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Session Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-primary flex items-center gap-2">
                <Clock className="w-4 h-4 text-[var(--color-primary)]" /> Session Type *
              </label>
              <select
                name="sessionType"
                required
                className="w-full p-3 rounded-lg border border-primary/10 focus:ring-2 focus:ring-primary/20 outline-none"
                onChange={handleChange}
              >
                <option value="">Select Option</option>
                {isDelivered ? (
                  <>
                    <option value="3_questions">3 Questions - {getPriceDisplay('delivered-3')}</option>
                    <option value="5_questions">5 Questions - {getPriceDisplay('delivered-5')}</option>
                  </>
                ) : (
                  <>
                    <option value="20_min">20 Minutes - {getPriceDisplay('live-20')}</option>
                    <option value="40_min">40 Minutes - {getPriceDisplay('live-40')}</option>
                  </>
                )}
              </select>
            </div>

            {/* Reading Focus */}
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium text-primary flex items-center gap-2">
                <Heart className="w-4 h-4 text-[var(--color-primary)]" /> Reading Focus *
              </label>
              <select
                name="readingFocus"
                required
                className="w-full p-3 rounded-lg border border-primary/10 focus:ring-2 focus:ring-primary/20 outline-none"
                onChange={handleChange}
              >
                <option value="">Select Topic</option>
                <option value="love">Love & Relationships</option>
                <option value="career">Career & Finance</option>
                <option value="other">Other / Mixed</option>
              </select>
              <p className="text-xs text-amber-600 mt-1">Note: Questions related to serious health conditions/illnesses are not accepted.</p>
            </div>

            {/* Partner Details */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-primary">Partner/Other Person's Name (Optional)</label>
              <input
                type="text"
                name="partnerName"
                className="w-full p-3 rounded-lg border border-primary/10 focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="If applicable"
                onChange={handleChange}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-primary">Their DOB (Optional)</label>
              <input
                type="date"
                name="partnerDob"
                className="w-full p-3 rounded-lg border border-primary/10 focus:ring-2 focus:ring-primary/20 outline-none"
                onChange={handleChange}
              />
            </div>


          </>
        )}

        {/* Aura Specific Fields */}
        {isAura && (
          <div className="md:col-span-2 space-y-4">

            {/* Minimalist Upload Header */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-primary flex items-center gap-2">
                <Upload className="w-4 h-4 text-[var(--color-primary)]" /> Upload Your Photo *
              </label>
            </div>

            {/* Main Upload Box - Clean & Centered */}
            <div className="border-2 border-dashed border-primary/20 rounded-2xl p-8 hover:border-purple-400 hover:bg-primary/5 transition-all group cursor-pointer relative bg-card items-center justify-center flex flex-col gap-3">
              <input
                type="file"
                name="picture"
                accept="image/*"
                required
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      alert("File size must be less than 5MB");
                      e.target.value = "";
                      return;
                    }
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setFormData(prev => ({ ...prev, auraImage: reader.result }));
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />

              {formData.auraImage ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <ShieldCheck className="w-8 h-8" />
                  </div>
                  <p className="font-medium text-primary">Photo Uploaded Successfully</p>
                  <p className="text-xs text-muted-foreground mt-1">Ready to submit</p>
                </div>
              ) : (
                <div className="text-center group-hover:scale-105 transition-transform duration-300">
                  <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                    <Smartphone className="w-7 h-7" />
                  </div>
                  <p className="font-semibold text-primary">Click to upload your photo</p>
                  <p className="text-xs text-muted-foreground mt-1">Supports JPG/PNG (Max 5MB)</p>
                </div>
              )}
            </div>

            {/* Collapsible Guidelines */}
            <details className="group rounded-xl border border-primary/5 bg-primary/5 open:bg-card open:shadow-sm open:border-primary/10 transition-all duration-300">
              <summary className="flex items-center gap-3 p-4 cursor-pointer list-none text-sm font-medium text-primary/80 hover:text-purple-700 select-none">
                <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">i</div>
                View Photo Guidelines & Example
                <span className="ml-auto transform group-open:rotate-180 transition-transform duration-300">
                  ▼
                </span>
              </summary>

              <div className="p-6 pt-0 border-t border-transparent group-open:border-primary/10 animate-in fade-in duration-300">
                <div className="flex flex-col md:flex-row gap-6 mt-4">
                  {/* Reference Image */}
                  <div className="w-full md:w-32 flex-shrink-0">
                    <div className="aspect-[3/4] rounded-lg overflow-hidden border-2 border-white shadow-md relative">
                      <img
                        src="/assets/aura_reference.jpeg"
                        alt="Example"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-[10px] text-center text-muted-foreground mt-1">Reference Style</p>
                  </div>

                  {/* Text Rules */}
                  <div className="flex-1">
                    <h4 className="font-bold text-primary text-sm mb-2">For Best Results:</h4>
                    <ul className="space-y-2 text-xs text-primary/80">
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 font-bold">✓</span>
                        Use a plain, light-colored background
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 font-bold">✓</span>
                        Ensure even lighting on your face and body
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 font-bold">✓</span>
                        Keep a natural, relaxed posture
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 font-bold">✓</span>
                        Wear simple, solid-colored clothing
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </details>
          </div>
        )}

        {/* Live Specific - Slot Selection */}
        {isLive && (
          <div className="md:col-span-2 space-y-4">
            <div className="space-y-2">
              {/* Booking Date Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-primary flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[var(--color-primary)]" /> Select Booking Date *
                </label>
                <input
                  type="date"
                  name="bookingDate"
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full p-3 rounded-lg border border-primary/20 focus:ring-2 focus:ring-blue-200 outline-none"
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-primary flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[var(--color-primary)]" /> Select Available Slot ({userTimezone}) *
                  </label>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Globe className="w-3 h-3" />
                    <span>{userTimezone}</span>
                  </div>
                </div>

                {loadingSlots ? (
                  <div className="text-sm text-muted-foreground italic">Loading slots...</div>
                ) : !formData.sessionType ? (
                  <div className="text-sm text-muted-foreground italic">Please select a session type to view duration-specific slots.</div>
                ) : !formData.bookingDate ? (
                  <div className="text-sm text-muted-foreground italic">Please select a date to view available slots.</div>
                ) : filteredSlots.length === 0 ? (
                  <div className="text-sm text-red-500 bg-red-50 p-4 rounded-lg border border-red-100">
                    No {formData.isEmergency ? 'emergency' : 'regular'} slots available for this date.
                    {formData.isEmergency ? ' Emergency slots must be within the next 24 hours (or next day).' : ' Please check another date.'}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {filteredSlots.map((slot) => {
                      // Logic to display slot time in USER Timezone

                      let displayTime = slot.time;
                      let utcIso = null;

                      // Check if backend returned UTC ISO (Target State)
                      if (slot.start_time_utc) {
                        utcIso = slot.start_time_utc;
                        displayTime = formatInTimeZone(utcIso, userTimezone, 'HH:mm');
                      } else {
                        // Fallback: Construct ISO assuming Business Time (Europe/Rome)
                        const businessTime = dayjs.tz(`${slot.date}T${slot.time}:00`, "Europe/Rome");

                        // Display in User Timezone
                        displayTime = businessTime.tz(userTimezone).format('HH:mm');

                        // Prepare UTC for proper submission
                        utcIso = businessTime.utc().format();
                      }

                      const slotValue = utcIso;
                      const isTomorrow = slot.date !== formData.bookingDate;

                      return (
                        <button
                          key={slot.id || slotValue}
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, slot: slotValue }));
                          }}
                          className={`p-3 rounded-lg border text-sm font-medium transition-all flex flex-col items-center justify-center ${formData.slot === slotValue
                            ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                            : 'bg-card text-primary border-primary/20 hover:border-blue-500 hover:bg-blue-50'
                            }`}
                        >
                          <div className="text-lg">{displayTime}</div>
                          <div className="text-[10px] opacity-70">({userTimezone.split('/')[1] || "Local"})</div>
                          {isTomorrow && <div className="text-xs font-bold text-amber-300">Next Day</div>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3 bg-red-50 p-4 rounded-xl border border-red-100">
              <input
                type="checkbox"
                name="isEmergency"
                id="isEmergency"
                checked={formData.isEmergency}
                onChange={handleChange}
                className="w-5 h-5 text-red-500 rounded focus:ring-red-500 border-primary/20"
              />
              <label htmlFor="isEmergency" className="text-sm text-primary cursor-pointer">
                <span className="font-semibold text-red-600 block">Request Emergency Slot</span>
                <span className="text-xs">Prioritized scheduling (Higher Rate). Includes next day slots.</span>
              </label>
            </div>
          </div>
        )}

        {/* Text Areas */}
        {!isAura && !isTikTokLive && (
          <>
            {!isLive && (
              <div className="md:col-span-2 space-y-2">
                <label className="text-sm font-medium text-primary flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-[var(--color-primary)]" /> Your Questions *
                </label>
                <textarea
                  name="questions"
                  required
                  className="w-full p-3 rounded-lg border border-primary/10 focus:ring-2 focus:ring-primary/20 outline-none min-h-[100px]"
                  placeholder="Type your questions here (Max 300 words)..."
                  onChange={handleChange}
                ></textarea>
                <p className="text-xs text-muted-foreground text-right">Max 300 words</p>
              </div>
            )}

            <div className="md:col-span-2 space-y-2">
              <label className="text-sm font-medium text-primary flex items-center gap-2">
                <FileText className="w-4 h-4 text-[var(--color-primary)]" /> {isLive ? 'Brief Situation Overview' : 'Your Story & Current Energy'} *
              </label>
              <textarea
                name="situation"
                required
                className="w-full p-3 rounded-lg border border-primary/10 focus:ring-2 focus:ring-primary/20 outline-none min-h-[150px]"
                placeholder={isLive
                  ? "Share a short summary of your situation to give me context before our session. This allows our 1:1 Zoom reading to focus entirely on questions and answers."
                  : "Describe your situation in your own words. This helps me understand your current energy and offer guidance that’s aligned with you."}
                onChange={handleChange}
              ></textarea>
              <p className="text-xs text-muted-foreground text-right">Max 500 words</p>
            </div>
          </>
        )}
      </div>

      <div className="pt-6">
        <Button type="submit" className="w-full btn-primary py-4 text-lg">
          Proceed to Payment
        </Button>
      </div>
    </form >
  );
};